import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  tableCellClasses,
} from "@mui/material";
import useControlAttribute from "../../../hooks/useControlAttribute";
import { IControlBase, TPhotoVoltaicEnergyValueAttribute } from "../types";
import { useTranslation } from "react-i18next";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 14,
    fontWeight: "bold",
    padding: "2px 8px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "2px 8px",
  },
}));

const EnergyTableCell = (props: {
  control: IControlBase;
  attribute: TPhotoVoltaicEnergyValueAttribute;
}) => {
  const { control, attribute } = props;
  const value = useControlAttribute(control, attribute).toFixed(2);
  return <StyledTableCell align="right">{value}</StyledTableCell>;
};

type TEnergyRowValues = {
  header: string;
  attributes: TPhotoVoltaicEnergyValueAttribute[];
};

const EnergyTableRow = (props: {
  control: IControlBase;
  values: TEnergyRowValues;
}) => {
  const { control, values } = props;
  return (
    <TableRow sx={{ padding: 0 }}>
      <StyledTableCell sx={{ fontWeight: "bold" }}>
        {values.header}
      </StyledTableCell>
      {values.attributes.map((attribute, index) => (
        <EnergyTableCell key={index} control={control} attribute={attribute} />
      ))}
    </TableRow>
  );
};

const EnergyTable = (props: { control: IControlBase }) => {
  const { control } = props;
  const { t } = useTranslation();

  const EnergyTableRows: TEnergyRowValues[] = [
    {
      header: t("PV"),
      attributes: [
        "energy_pv_today",
        "energy_pv_month",
        "energy_pv_year",
        "energy_pv_total",
      ],
    },
    {
      header: t("Load"),
      attributes: [
        "energy_total_load_today",
        "energy_total_load_month",
        "energy_total_load_year",
        "energy_total_load_total",
      ],
    },
    {
      header: t("Buy"),
      attributes: [
        "energy_feedin_today",
        "energy_feedin_month",
        "energy_feedin_year",
        "energy_feedin_total",
      ],
    },
    {
      header: t("Sell"),
      attributes: [
        "energy_sell_today",
        "energy_sell_month",
        "energy_sell_year",
        "energy_sell_total",
      ],
    },
  ];

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["", "Today", "Month", "Year", "Total"].map((header, index) => (
              <StyledTableCell key={index} align="right">
                {t(header)}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {EnergyTableRows.map((rowValues, index) => (
            <EnergyTableRow key={index} control={control} values={rowValues} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EnergyTable;
